import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { cn, formatCurrency } from '../../lib/utils';
import { Search, Plus, Filter, Calendar, User, FileText, Clock, CheckCircle2, AlertTriangle, MoreHorizontal, Sparkles, Coins, Eye, Stethoscope } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_ENCOUNTERS = [
  { id: 'ENC-001', patient: 'Sarah Johnson', patientId: '1', provider: 'Dr. Wilson', dos: '2026-01-30', type: 'Office Visit', status: 'ready', charges: 255.00, cpts: ['99214', '87880'] },
  { id: 'ENC-002', patient: 'Michael Brown', patientId: '2', provider: 'Dr. Chen', dos: '2026-01-30', type: 'Follow-up', status: 'pending_doc', charges: 185.00, cpts: ['99213'] },
  { id: 'ENC-003', patient: 'Emily Davis', patientId: '3', provider: 'Dr. Roberts', dos: '2026-01-29', type: 'New Patient', status: 'billed', charges: 350.00, cpts: ['99204', '93000'] },
  { id: 'ENC-004', patient: 'Robert Wilson', patientId: '4', provider: 'Dr. Thompson', dos: '2026-01-29', type: 'Procedure', status: 'ready', charges: 1250.00, cpts: ['20610', '76942'] },
  { id: 'ENC-005', patient: 'Lisa Anderson', patientId: '5', provider: 'Dr. Wilson', dos: '2026-01-29', type: 'Office Visit', status: 'review', charges: 195.00, cpts: ['99214'] },
  { id: 'ENC-006', patient: 'James Taylor', patientId: '6', provider: 'Dr. Park', dos: '2026-01-28', type: 'Annual Exam', status: 'billed', charges: 285.00, cpts: ['99395', '36415'] },
  { id: 'ENC-007', patient: 'Maria Garcia', patientId: '7', provider: 'Dr. Chen', dos: '2026-01-28', type: 'Sick Visit', status: 'pending_doc', charges: 165.00, cpts: ['99213'] },
  { id: 'ENC-008', patient: 'David Miller', patientId: '8', provider: 'Dr. Roberts', dos: '2026-01-27', type: 'Consult', status: 'billed', charges: 425.00, cpts: ['99244'] },
];

const RevQEncounters = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getStatusBadge = (status) => {
    const configs = {
      ready: { label: 'Ready to Bill', class: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
      pending_doc: { label: 'Pending Documentation', class: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      review: { label: 'Needs Review', class: 'bg-blue-50 text-blue-700 border-blue-200', icon: AlertTriangle },
      billed: { label: 'Billed', class: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
    };
    const config = configs[status] || configs.ready;
    const Icon = config.icon;
    return <Badge className={cn("gap-1", config.class)}><Icon className="h-3 w-3" />{config.label}</Badge>;
  };

  const filteredEncounters = DEMO_ENCOUNTERS.filter(enc => {
    const matchesSearch = enc.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         enc.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || enc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    ready: DEMO_ENCOUNTERS.filter(e => e.status === 'ready').length,
    pendingDoc: DEMO_ENCOUNTERS.filter(e => e.status === 'pending_doc').length,
    review: DEMO_ENCOUNTERS.filter(e => e.status === 'review').length,
    totalCharges: DEMO_ENCOUNTERS.filter(e => e.status === 'ready').reduce((sum, e) => sum + e.charges, 0)
  };

  return (
    <div className="space-y-6" data-testid="encounters-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Encounters</h1>
          <p className="text-slate-500 mt-1">Manage patient encounters and prepare for billing</p>
        </div>
        <Button className="bg-black hover:bg-gray-800 gap-2">
          <Plus className="h-4 w-4" /> New Encounter
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ready to Bill</p>
                <p className="text-xl font-bold text-emerald-600">{stats.ready}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Docs</p>
                <p className="text-xl font-bold text-amber-600">{stats.pendingDoc}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Needs Review</p>
                <p className="text-xl font-bold">{stats.review}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ready Charges</p>
                <p className="text-xl font-bold">{formatCurrency(stats.totalCharges)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search encounters..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ready">Ready to Bill</SelectItem>
                <SelectItem value="pending_doc">Pending Docs</SelectItem>
                <SelectItem value="review">Needs Review</SelectItem>
                <SelectItem value="billed">Billed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* AI Tools Bar */}
      <Card className="border-violet-200 bg-gradient-to-r from-violet-50/50 to-white" data-testid="ai-tools-bar">
        <CardContent className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center"><Sparkles className="h-4 w-4 text-violet-600" /></div>
            <div>
              <p className="text-sm font-medium">AI Billing Tools</p>
              <p className="text-[11px] text-muted-foreground">{stats.ready} encounters ready — use AI to review codes, validate charges, or generate claims</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => toast.success('AI reviewing all codes...', { description: 'Uses 1 credit per encounter' })}><Sparkles className="h-3 w-3" /> AI Code Review <Badge className="bg-slate-100 text-slate-500 text-[9px]"><Coins className="h-2.5 w-2.5 mr-0.5" />1</Badge></Button>
            <Button size="sm" className="bg-black hover:bg-gray-800 text-xs gap-1.5" onClick={() => navigate('/revq/ai-claims')}><Sparkles className="h-3 w-3" /> Batch AI Claims <Badge className="bg-white/20 text-white text-[9px]"><Coins className="h-2.5 w-2.5 mr-0.5" />3</Badge></Button>
          </div>
        </CardContent>
      </Card>

      {/* Encounters Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">All Encounters ({filteredEncounters.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Encounter ID</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>DOS</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>CPT Codes</TableHead>
                <TableHead className="text-right">Charges</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEncounters.map((enc) => (
                <TableRow key={enc.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-sm">{enc.id}</TableCell>
                  <TableCell>
                    <button onClick={() => navigate(`/revq/patients/${enc.patientId}`)} className="font-medium text-slate-800 hover:text-violet-600 hover:underline transition-colors cursor-pointer" data-testid={`enc-patient-${enc.id}`}>{enc.patient}</button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{enc.provider}</TableCell>
                  <TableCell>{enc.dos}</TableCell>
                  <TableCell>{enc.type}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {enc.cpts.map(cpt => (
                        <Badge key={cpt} variant="outline" className="font-mono text-xs">{cpt}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(enc.charges)}</TableCell>
                  <TableCell>{getStatusBadge(enc.status)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => navigate(`/revq/patients/${enc.patientId}`)} className="gap-2"><Eye className="h-4 w-4" /> View Patient Chart</DropdownMenuItem>
                        <DropdownMenuItem className="gap-2"><FileText className="h-4 w-4" /> View Details</DropdownMenuItem>
                        <DropdownMenuItem className="gap-2"><Sparkles className="h-4 w-4" /> AI Code Review <Badge className="ml-auto bg-slate-100 text-slate-600 text-[9px] font-normal">1 cr</Badge></DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => navigate('/revq/ai-claims')} className="gap-2"><Sparkles className="h-4 w-4" /> Send to AI Claims <Badge className="ml-auto bg-slate-100 text-slate-600 text-[9px] font-normal">3 cr</Badge></DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => toast.success('Claim created')} className="gap-2"><FileText className="h-4 w-4" /> Create Claim</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => navigate(`/lumina/patients/${enc.patientId}`)} className="gap-2 text-blue-600"><Stethoscope className="h-4 w-4" /> Open in Lumina</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevQEncounters;
